import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import TimeMachineStatusProvider from '@/features/timeMachine/components/TimeMachineStatusProvider';
import { TranscodingProfileProvider } from '@/shared/context/TranscodingProfileContext';
import { ExtendSessionDialog } from '@/shared/components';
import { LocalSessionState, useAuth } from './AuthContext';
import { AuthProvider } from './AuthContext';
import UserStateProvider from '@/shared/components/UserStateProvider';
import { RealtimeEventsProvider } from '@/features/realtime';
import { getSignInUrl } from '@/features/authentication/functions/returnUrl';

type Props = {
  children: ReactNode;
};

// Inner component that has access to AuthContext
const AuthenticatedContent = ({ children }: Props) => {
  const { sessionState, isExpiringSession, setIsExpiringSession, getToken } = useAuth();
  const location = useLocation();
  const [token, setToken] = useState<string | undefined>();

  // Update token when it changes
  useEffect(() => {
    if (getToken) {
      const currentToken = getToken();
      setToken(currentToken);
    }
  }, [getToken]);

  if (sessionState === LocalSessionState.NO_LOCAL_SESSION) {
    return <Navigate to={getSignInUrl(location)} />;
  }

  return (
    <RealtimeEventsProvider token={token}>
      <UserStateProvider>
        <TimeMachineStatusProvider>
          <ExtendSessionDialog
            open={isExpiringSession}
            onClose={() => setIsExpiringSession(false)}
          />
          <TranscodingProfileProvider>{children}</TranscodingProfileProvider>
        </TimeMachineStatusProvider>
      </UserStateProvider>
    </RealtimeEventsProvider>
  );
};

const AuthenticateRoute = ({ children }: Props) => {
  return (
    <AuthProvider>
      <AuthenticatedContent>{children}</AuthenticatedContent>
    </AuthProvider>
  );
};

export default AuthenticateRoute;
